import React, { CSSProperties, useState } from 'react';
import {
    getCookie,
    setCookie,
    getCircularReplacer,
    getUrlParam,
    styx,
    clsx,
    utilsGet,
    findKeyPath,
} from 'modules/Utils';
import SwitchSymphony from '../../elements/SwitchSymphony';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import Axios from "helpers/Interceptor"
import ReactJson, { ReactJsonViewProps } from 'react-json-view';
import Loader from 'elements/Loader';
import { useHistory, useLocation } from 'react-router-dom';

const JSON_COLLAPSED = 2;

// Register the JSON language
SyntaxHighlighter.registerLanguage('json', json);

interface DebugBoxProps {
    className?: string,
    style?: CSSProperties,
    varName?: string,
    filePath?: string,
    data?: any,
    alwaysShow?: boolean,
    mode?: string,
    dHeight?: number,
    isApi?: boolean,
    onClick?: () => void,
}

/** Type for the debug body content that can be either a string or array of React nodes */
type DebugBodyContent = string | React.ReactNode[] | React.ReactElement | null;

const DebugBox = ({
    className = '',
    style = {},
    varName,
    filePath,
    data,
    alwaysShow,
    mode,
    dHeight = Number(getUrlParam('dHeight', 600)),
    isApi = false,
    onClick,
}: DebugBoxProps) => {
    const history = useHistory();
    const location = useLocation();
    
    const [displayMode, setDisplayMode] = useState(mode ? mode : 'brief');
    const [filterText, setFilterText] = useState(getCookie('debugFilterText', ''));
    const [filterValueText, setFilterValueText] = useState(getCookie('debugFilterValueText', ''));
    const [flexDirection, setFlexDirection] = useState(getCookie('debugFlexDirection', 'row'));
    const [persistOriginalData, setPersistOriginalData] = useState(getCookie('debugPersistOriginalData', 'false') === 'true');
    const [routeText, setRouteText] = useState(getCookie('debugRouteText', ''));
    const [isNumber, setIsNumber] = useState(getCookie('debugIsNumber', 'false') === 'true');
    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<any>(null);
    const [jsonInput, setJsonInput] = useState('{}');

    const toggleRowColumn = () => {
        const newFlexDirection = flexDirection === 'row' ? 'column' : 'row';
        setCookie('debugFlexDirection', newFlexDirection);
        setFlexDirection(newFlexDirection);
    }

    const toggleIsNumber = () => {
        setCookie('debugIsNumber', (!isNumber).toString());
        setIsNumber(!isNumber);
    }

    const togglePersistOriginalData = () => {
        setCookie('debugPersistOriginalData', (!persistOriginalData).toString());
        setPersistOriginalData(!persistOriginalData);
    }

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFilterText = e.target.value;
        setCookie('debugFilterText', newFilterText);
        setFilterText(newFilterText);
    }

    const handleFilterValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFilterValueText = e.target.value;
        setCookie('debugFilterValueText', newFilterValueText);
        setFilterValueText(newFilterValueText);
    }

    const handleRouteTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRouteText = e.target.value;
        setCookie('debugRouteText', newRouteText);
        setRouteText(newRouteText);
    }

    const makeRequest = async (method: string) => {
        try {
            setIsLoading(true);
            let data = {};
            try {
                data = JSON.parse(jsonInput);
            } catch (e) {
                setResponse({ error: "Invalid JSON input" });
                setIsLoading(false);
                return;
            }
            let res = null
            switch (method) {
                case 'GET':
                    res = await Axios.get(routeText);
                    break;
                case 'POST':
                    res = await Axios.post(routeText, data);
                    break;
                case 'PUT':
                    res = await Axios.put(routeText, data);
                    break;
                case 'DELETE':
                    res = await Axios.delete(routeText);
                    break;
            }

            setResponse(res);
        } catch (error: any) {
            setResponse(error.message || 'Error making request');
        }
        setIsLoading(false);
    }

    const generateLine = (utilsGetString: string, codeResult: string | React.ReactElement, i: number, isKey = false) => {
        return (
            <div
                key={`debugBox-${isKey ? 'key' : 'value'}-${i}`}
                className={`debugFilterSection-${i}`}
                style={{ ...sty.debugFilterSection, ...(i % 2 ? sty.section1 : sty.section2) }}
            >
                <div className="utilsGetStringContainer" style={sty.utilsGetStringContainer}>
                    {isKey ? 'KEY PATH' : 'VALUE'}: <span style={{ fontWeight: 400, marginLeft: 4 }}>{utilsGetString}</span>
                </div>
                <div style={sty.codeSectionContainer}>
                    {codeResult}
                </div>
            </div>
        )
    }

    /** Formats the data based on debug mode and filter settings */
    const getFormattedBody = (): DebugBodyContent => {
        if (!showBox || !showData) {
            return null;
        }

        const reactJsonDefaultProps: any = {
            theme: 'monokai',
            style: sty.jsonViewer,
            collapsed: JSON_COLLAPSED,
            enableClipboard: true,
            displayDataTypes: false,
            name: false,
            indentWidth: 6,
            sortKeys: true,
        }

        // Handle basic JSON stringify modes
        if (debug === '12') {
            return (
                <ReactJson
                    src={showData}
                    {...reactJsonDefaultProps}
                />
            );
        }

        if (debug === '13') {
            const originalData = (
                <ReactJson
                    src={showData}
                    {...reactJsonDefaultProps}
                />
            );

            if (!filterText && !filterValueText) {
                return originalData;
            }

            const bodyElements: React.ReactNode[] = [];

            // Process key path filters
            if (filterText) {
                filterText.split(', ').forEach((utilsGetString: string, i: number) => {
                    const codeResult = (
                        <ReactJson
                            src={utilsGet(showData, utilsGetString)}
                            {...reactJsonDefaultProps}
                        />
                    );
                    bodyElements.push(generateLine(utilsGetString, codeResult, i, true));
                });
            }

            // Process value filters
            if (filterValueText) {
                filterValueText.split(', ').forEach((valueString: string, i: number) => {
                    const result = (
                        <SyntaxHighlighter
                            key={`value-filter-${i}-${valueString}`}
                            language="json"
                            style={atomOneDark}
                            customStyle={sty.syntaxHighlighter}
                        >
                            {String(findKeyPath(showData, valueString, isNumber))}
                        </SyntaxHighlighter>
                    );
                    bodyElements.push(generateLine(valueString, result, i));
                });
            }

            if (persistOriginalData) {
                bodyElements.unshift(originalData);
            }

            return bodyElements;
        }

        return null;
    };

    const debug = alwaysShow ? '13' : getUrlParam('debug');
    const debugVar = getUrlParam('debugVar');
    const showBox = debug === '12' || debug === '13' || alwaysShow;
    const showData = isApi ? response : (debugVar ? data[debugVar] : data);

    if (getUrlParam('dConsole') === '13') {
        console.log('DebugBox', showData);
    }

    if (!showBox || location.pathname.includes('beta.symphonyos.co')) {
        return null;
    }

    const body = getFormattedBody();

    const renderApiSection = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                    <input
                        className="debugboxRouteInput"
                        style={sty.debugboxFilterInput}
                        type="text"
                        placeholder="API route"
                        onChange={handleRouteTextChange}
                        value={routeText}
                    />
                    {isLoading ?
                        <div style={{ width: 32, paddingTop: 6 }}>
                            <Loader size={20} />
                        </div>
                        :
                        <div style={{ width: 32, height: 20 }} />
                    }
                    <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={() => makeRequest('GET')}
                        disabled={isLoading}
                        size="small"
                    >
                        GET
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={() => makeRequest('POST')}
                        disabled={isLoading}
                        size="small"
                        color="green"
                    >
                        POST
                    </ButtonSymphony>
                    {/* <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={() => makeRequest('PATCH')}
                        disabled={isLoading}
                        size="small"
                        color="yellow"
                    >
                        PATCH
                    </ButtonSymphony> */}
                    <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={() => makeRequest('PUT')}
                        disabled={isLoading}
                        size="small"
                        color="yellow"
                    >
                        PUT
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={() => makeRequest('DELETE')}
                        disabled={isLoading}
                        size="small"
                        color="red"
                    >
                        DELETE
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="debugboxHeaderButton"
                        onClick={onClick}
                        disabled={isLoading}
                        size="small"
                        color="white"
                    >
                        CLICK
                    </ButtonSymphony>
                </div>
                <textarea
                    className="jsonInputTextarea"
                    style={sty.jsonTextarea}
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder="Enter JSON data..."
                    spellCheck="false"
                />
            </div>
        )
    }

    return (
        <div
            className={clsx('debugboxComponent', className)}
            style={styx(
                sty.debugboxComponent,
                style,
                { maxHeight: dHeight }
            )}
        >
            {isApi &&
                renderApiSection()
            }
            <div className="debugboxHeader" style={sty.debugboxHeader}>
                {(filePath || varName) &&
                    <div className="debugboxHeaderContent" style={sty.debugboxHeaderContent}>
                        {filePath &&
                            <div className="debugboxFilePathText" style={sty.debugboxFilePathText}>
                                <span style={{ fontWeight: 500, color: Colors.green }}>filePath:</span> {filePath}
                            </div>
                        }
                        {varName &&
                            <div className="debugboxVarName" style={sty.debugboxVarName}>
                                <span style={{ fontWeight: 500, color: Colors.green }}>varName:</span> {varName}
                            </div>
                        }
                    </div>
                }
                {debug === '13' &&
                    <>
                        <input
                            className="debugboxFilterKeyInput"
                            style={sty.debugboxFilterInput}
                            type="text"
                            placeholder="Search Key"
                            onChange={handleFilterChange}
                            value={filterText}
                        />
                        <input
                            className="debugboxFilterValueInput"
                            style={{ ...sty.debugboxFilterInput, ...{ maxWidth: 200 } }}
                            type="text"
                            placeholder="Search Value"
                            onChange={handleFilterValueChange}
                            value={filterValueText}
                        />
                    </>
                }
                <div className="checkboxContainer" style={sty.checkboxContainer}>
                    <SwitchSymphony
                        className="debugboxIsNumberCheckbox"
                        checked={isNumber}
                        onChange={toggleIsNumber}
                        label="isNum"
                        labelPlacement="bottom"
                        isDarkMode
                        labelStyle={{ fontSize: 12 }}
                    />
                    <SwitchSymphony
                        className="debugboxFlexDirectionCheckbox"
                        checked={flexDirection === 'row'}
                        onChange={toggleRowColumn}
                        label={flexDirection === 'row' ? 'row' : 'col'}
                        labelPlacement="bottom"
                        isDarkMode
                        labelStyle={{ fontSize: 12 }}
                    />
                    <SwitchSymphony
                        className="debugboxPersistOriginalDataCheckbox"
                        checked={persistOriginalData}
                        onChange={togglePersistOriginalData}
                        label="persist"
                        labelPlacement="bottom"
                        isDarkMode
                        labelStyle={{ fontSize: 12 }}
                    />
                </div>
            </div>
            <pre
                className="preDebugCode"
                style={styx(
                    sty.preDebugCode,
                    { flexDirection: flexDirection === 'row' ? 'row' : 'column' }
                )}
            >
                {body}
            </pre>
        </div>
    );
}

const sty: Record<string, CSSProperties> = {
    debugboxComponent: {
        width: '100%',
        padding: "0px 10px",
        background: "black",
        overflow: "scroll",
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        color: "white",
        borderRadius: 8,
        textAlign: 'left',
    },
    debugboxHeader: {
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        paddingTop: 8,
        paddingBottom: 8,
        gap: 16,
        top: 0,
        position: 'sticky',
        zIndex: 1,
    },
    debugboxFilePathText: {
        fontSize: 16,
        fontWeight: 500,
    },
    debugboxVarName: {
        fontSize: 14,
        fontWeight: 400,
    },
    debugboxFilterInput: {
        background: 'rgba(255,255,255,0.2)',
        padding: 4,
        borderRadius: 4,
        fontSize: 14,
        color: 'white',
        marginBottom: 0,
        fontWeight: 400,
        width: '100%',
    },
    section1: {
        background: 'rgba(255,255,255,0.17)',
    },
    section2: {
        background: 'rgba(255,255,255,0.12)',
    },
    checkboxContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 24,
        marginTop: 8,
    },
    preDebugCode: {
        display: 'flex',
        fontSize: 14,
        gap: 4,
        margin: 0,
        paddingBottom: 10,
    },
    debugFilterSection: {
        padding: 8,
        borderRadius: 4,
        overflowX: 'scroll',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        flex: 1,
    },
    utilsGetStringContainer: {
        fontWeight: 'bold',
        position: 'sticky',
        left: 0,
    },
    codeSectionContainer: {
        paddingTop: 8,
    },
    syntaxHighlighter: {
        background: 'transparent',
        margin: 0,
        padding: 0,
        flex: 1,
    },
    jsonTextarea: {
        width: '100%',
        minHeight: 200,
        padding: 8,
        margin: 0,
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 4,
        background: 'rgba(255,255,255,0.1)',
        color: 'white',
        fontFamily: 'Menlo, Monaco, Consolas, monospace',
        fontSize: 13,
        lineHeight: '20px',
        resize: 'vertical',
    },
    jsonViewer: {
        background: 'transparent',
        padding: 0,
        fontFamily: 'Menlo, Monaco, Consolas, monospace',
        fontSize: 13,
    },
}

export default DebugBox;